import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { format } from 'date-fns';
import { toast } from 'sonner';
import RecurrenceBuilder, { DEFAULT_RECURRENCE, type RecurrenceData } from '@/components/RecurrenceBuilder';
import { normalizeHealthSubjectRecord } from '../../../ui-foundation/features/health';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppButtonRow,
  AppCardBody,
  AppFieldGroup,
  AppInput,
  AppNativeSelect,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { TYPE_ICONS, TYPE_LABELS, type HealthReminder, type HealthSubjectOption, type ReminderType } from './health-shared';
import AppDateField from '@/components/AppDateField';

const TYPES: ReminderType[] = [
  'gp',
  'dentist',
  'optician',
  'smear',
  'mammogram',
  'health-check',
  'prostate',
  'hrt-review',
  'blood-test',
  'medication',
  'vaccination',
  'other',
];

interface ReminderDraft {
  type?: ReminderType;
  title?: string;
  notes?: string | null;
  dueDate?: string | null;
  dueTime?: string | null;
  reminderDaysBefore?: number | null;
  recurrence?: RecurrenceData | null;
  subjectValue?: string;
}

interface Props {
  onClose: () => void;
  onSaved: () => void;
  editReminder?: HealthReminder | null;
  draftReminder?: ReminderDraft | null;
  subjectOptions: HealthSubjectOption[];
}

function resolveSubjectValue(subjectOptions: HealthSubjectOption[], editReminder?: HealthReminder | null, draftReminder?: ReminderDraft | null) {
  if (draftReminder?.subjectValue && subjectOptions.some((option) => option.value === draftReminder.subjectValue)) {
    return draftReminder.subjectValue;
  }

  const normalized = normalizeHealthSubjectRecord({
    subjectType: editReminder?.subject_type,
    subjectId: editReminder?.subject_id,
    subjectName: editReminder?.subject_name,
  });

  const match = subjectOptions.find((option) =>
    option.subjectType === normalized.subjectType && option.subjectId === normalized.subjectId
  );

  return match?.value || subjectOptions[0]?.value || 'self';
}

export default function HealthReminderForm({ onClose, onSaved, editReminder, draftReminder, subjectOptions }: Props) {
  const { user } = useApp();
  const isEdit = !!editReminder;

  const [type, setType] = useState<ReminderType>(editReminder?.type || draftReminder?.type || 'gp');
  const [title, setTitle] = useState(editReminder?.title || draftReminder?.title || '');
  const [dueDate, setDueDate] = useState<Date | undefined>(
    editReminder?.due_date
      ? new Date(editReminder.due_date)
      : draftReminder?.dueDate
        ? new Date(draftReminder.dueDate)
        : undefined
  );
  const [dueTime, setDueTime] = useState(editReminder?.due_time || draftReminder?.dueTime || '');
  const [notes, setNotes] = useState(editReminder?.notes || draftReminder?.notes || '');
  const [subjectValue, setSubjectValue] = useState(() => resolveSubjectValue(subjectOptions, editReminder, draftReminder));
  const [reminderDaysBefore, setReminderDaysBefore] = useState(
    editReminder?.reminder_days_before != null
      ? String(editReminder.reminder_days_before)
      : draftReminder?.reminderDaysBefore != null
        ? String(draftReminder.reminderDaysBefore)
        : ''
  );
  const [saving, setSaving] = useState(false);

  // Build recurrence from existing data or legacy frequency_months
  const initialRecurrence = (): RecurrenceData => {
    if (editReminder) {
      if ((editReminder as any).recurrence_type === 'custom') {
        return {
          recurrence_type: 'custom',
          recurrence_interval: (editReminder as any).recurrence_interval,
          recurrence_unit: (editReminder as any).recurrence_unit,
          recurrence_day_of_week: (editReminder as any).recurrence_day_of_week,
          recurrence_week_of_month: (editReminder as any).recurrence_week_of_month,
          recurrence_month_day: (editReminder as any).recurrence_month_day,
        };
      }
      // Legacy: frequency_months
      if (editReminder.frequency_months) {
        return {
          recurrence_type: 'custom',
          recurrence_interval: editReminder.frequency_months,
          recurrence_unit: 'months',
          recurrence_day_of_week: null,
          recurrence_week_of_month: null,
          recurrence_month_day: null,
        };
      }
    }
    if (draftReminder?.recurrence) {
      return draftReminder.recurrence;
    }
    return DEFAULT_RECURRENCE;
  };

  const [recurrence, setRecurrence] = useState<RecurrenceData>(initialRecurrence);

  const canSave = title.trim().length > 0;
  const selectedSubject = subjectOptions.find((option) => option.value === subjectValue) || subjectOptions[0];

  const handleSave = async () => {
    if (!user || !canSave || !selectedSubject) return;
    setSaving(true);

    const payload = {
      user_id: user.id,
      type,
      title: title.trim(),
      due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
      due_time: dueTime || null,
      frequency_months: recurrence.recurrence_type === 'custom' && recurrence.recurrence_unit === 'months'
        ? recurrence.recurrence_interval : null,
      notes: notes.trim() || null,
      reminder_days_before: reminderDaysBefore === '' ? null : Number(reminderDaysBefore),
      subject_type: selectedSubject.subjectType,
      subject_id: selectedSubject.subjectId,
      subject_name: selectedSubject.subjectName,
      recurrence_type: recurrence.recurrence_type,
      recurrence_interval: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_interval : null,
      recurrence_unit: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_unit : null,
      recurrence_day_of_week: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_day_of_week : null,
      recurrence_week_of_month: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_week_of_month : null,
      recurrence_month_day: recurrence.recurrence_type === 'custom' ? recurrence.recurrence_month_day : null,
    } as any;

    const { error } = isEdit && editReminder
      ? await supabase.from('health_reminders').update(payload).eq('id', editReminder.id)
      : await supabase.from('health_reminders').insert(payload);

    if (error) {
      console.error(error);
      toast.error('Could not save your health reminder right now.');
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <AppBottomSheet onClose={onClose}>
      <AppBottomSheetHeader
        title={isEdit ? 'Edit health reminder' : 'Add health reminder'}
        subtitle="Keep appointments, medication reviews, and screening dates in the same shared reminder flow."
      />

      <AppCardBody className="flex-1 overflow-y-auto px-5 py-5" gap="$lg">
            <AppFieldGroup label="Type">
              <AppButtonRow>
                {TYPES.map((t) => (
                  <AppButton
                    key={t}
                    type="button"
                    size="sm"
                    tone={type === t ? 'secondary' : 'outline'}
                    onPress={() => setType(t)}
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minWidth={92}
                  >
                    <AppText fontSize="$lg" color={type === t ? '$secondaryForeground' : '$color'}>
                      {TYPE_ICONS[t]}
                    </AppText>
                    <AppText
                      fontSize="$xs"
                      fontWeight="700"
                      textAlign="center"
                      color={type === t ? '$secondaryForeground' : '$colorMuted'}
                    >
                      {TYPE_LABELS[t]}
                    </AppText>
                  </AppButton>
                ))}
              </AppButtonRow>
            </AppFieldGroup>

            <AppFieldGroup label="Title">
              <AppInput
                tag="input"
                value={title}
                onChange={(event) => setTitle((event.target as HTMLInputElement).value)}
                placeholder="e.g. Annual blood test"
              />
            </AppFieldGroup>

            <AppFieldGroup label="Who is this for?">
              <AppNativeSelect value={subjectValue} onChange={(event) => setSubjectValue(event.target.value)}>
                {subjectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppNativeSelect>
            </AppFieldGroup>

            <AppDateField
              label="Due date"
              value={dueDate ? format(dueDate, 'yyyy-MM-dd') : ''}
              onChange={(nextValue) => setDueDate(nextValue ? new Date(nextValue) : undefined)}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <AppFieldGroup label="Time (optional)">
                <AppInput
                  tag="input"
                  type="time"
                  value={dueTime}
                  onChange={(event) => setDueTime((event.target as HTMLInputElement).value)}
                />
              </AppFieldGroup>

              <AppFieldGroup label="Reminder timing">
                <AppNativeSelect value={reminderDaysBefore} onChange={(event) => setReminderDaysBefore(event.target.value)}>
                  <option value="">On the day or manual</option>
                  <option value="1">1 day before</option>
                  <option value="2">2 days before</option>
                  <option value="7">1 week before</option>
                  <option value="14">2 weeks before</option>
                  <option value="30">1 month before</option>
                </AppNativeSelect>
              </AppFieldGroup>
            </div>

            <AppFieldGroup label="Repeat">
              <RecurrenceBuilder value={recurrence} onChange={setRecurrence} />
            </AppFieldGroup>

            <AppFieldGroup label="Notes (optional)">
              <AppTextArea
                value={notes}
                onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                placeholder="Any notes..."
                rows={3}
              />
            </AppFieldGroup>

            <AppButtonRow justifyContent="flex-end">
              <AppButton tone="outline" onPress={onClose}>
                Cancel
              </AppButton>
              <AppButton onPress={handleSave} disabled={!canSave || saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save'}
              </AppButton>
            </AppButtonRow>
      </AppCardBody>
    </AppBottomSheet>
  );
}
